/**
 * @file Election/component.js
 * @copyright 2018 CBC/Radio-Canada. All rights reserved.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { pollElectionState } from "../../actions/Election/actions";
import { generateElectionData } from "../../actions/Election/record";
import LinkWrap from "../LinkWrap/component";
import classNames from "classnames";
import { normalizeClass } from "../../utils/path/library";
import { REFRESH_TIME } from "./constants";

import log from "../../utils/logger/library";

export const PartyList = (parties) => {
    return parties.map((party, i) => {
        if (!party.display) {
            return null;
        }
        const borderColor = {
            borderColor: party.partyHex,
        };
        const key = `${party.partyCode}-${party.totalElectedLeadingSeats}-${party.totalVotes}-${i}`;
        return (
            <div className="partybox" key={key}>
                <div className="partyname" style={borderColor}>
                    {party.partyCode}
                </div>
                <div className="partyseatnum">
                    {party.totalElectedLeadingSeats}
                </div>
                <div className="moreinfocontainer">
                    <div className="partyvotes">{party.totalVotes} votes</div>
                </div>
            </div>
        );
    });
};

export const CallToAction = ({ rsURL, widgetCTA, trackingLinkTitle }) => {
    if (rsURL && widgetCTA) {
        const trackingClass =
            (trackingLinkTitle &&
                normalizeClass("sclt", trackingLinkTitle, "link")) ||
            "";

        const ctaClasses = classNames({
            electionlink: true,
            [trackingClass]: true,
        });

        return (
            <LinkWrap className={ctaClasses} url={rsURL} external={true}>
                {widgetCTA}
            </LinkWrap>
        );
    }
    return null;
};

/**
 * Election component.
 *
 * @param {Object} props The provided component props
 * @returns {React.Component} The created React component
 */

export const Election = ({ province = "", trackingLinkTitle }) => {
    const [election, setElection] = useState(generateElectionData());

    useEffect(
        () => pollElectionState(REFRESH_TIME, province, setElection),
        [province]
    );

    if (election.parties.length < 1) {
        if (election.serverTime) {
            log.warn("The election widget has no parties configured.");
        }
        return null;
    }

    const title = election.widgetTitle;
    const liveFlag = election.live;
    const electionText = election.widgetDescription;

    // Display title location depending on viewport
    const liveFlagMarkup = liveFlag ? (
        <span className="pulse" aria-label="currently live"></span>
    ) : null;
    const titletopMarkup = title ? (
        <div className="top" data-testid="top-markup">
            {" "}
            {liveFlagMarkup} <h2 className="electiontitle">{title}</h2>
        </div>
    ) : null;
    const titlebottomMarkup = title ? (
        <div className="bottom" data-testid="bottom-markup">
            {" "}
            {liveFlagMarkup} <h2 className="electiontitle">{title}</h2>
        </div>
    ) : null;

    const electionTextMarkup = electionText ? (
        <p className="electiontext" data-testid="election-text">
            {electionText}
        </p>
    ) : null;

    const linkBox = (
        <div className="linkbox">
            {titlebottomMarkup}
            {electionTextMarkup}
            <CallToAction
                rsURL={election.rsURL}
                widgetCTA={election.widgetCTA}
                trackingLinkTitle={trackingLinkTitle}
            />
        </div>
    );

    return (
        <div
            className="election"
            role="region"
            aria-label={title}
            data-testid="election"
        >
            {titletopMarkup}
            {linkBox}
            <div className="partyboxcontainer">
                {PartyList(election.parties)}
            </div>
        </div>
    );
};

Election.propTypes = {
    //So far only allowing values on /actions/Elections/constants
    // that are on the VALID_PROVINCES array
    province: PropTypes.string,
    trackingLinkTitle: PropTypes.string,
};

export default Election;
